import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { PatchJob, PatchTask } from '../../api/client'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { BottoniEsporta } from '../BottoniEsporta'
import { DataTable } from '../DataTable'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime, timeAgo } from '../common'
import { ProgressStrip } from './Overview'
import { TaskDetail } from './TaskDetail'
import {
  JobPhaseChip, TaskStatusChip, VerificationChip, jobWhen, kindLabel, platformLabel, reasonLabel,
  rebootPolicy, useIsTenantAdmin,
} from './labels'

/**
 * The jobs: one row each with its progress; opening one shows every machine
 * with its status, the verification and the details of the installation.
 */
export function Jobs({ jobs, openJobId, canWrite, onChanged, onNewJob }: {
  jobs: PatchJob[]
  openJobId?: string
  canWrite: boolean
  onChanged: () => void
  onNewJob: () => void
}) {
  const [open, setOpen] = useState<string | null>(openJobId ?? null)
  useEffect(() => { if (openJobId) setOpen(openJobId) }, [openJobId])
  const single = useAsync(() => api.patchTasks(), [])
  const loose = (single.data ?? []).filter((x) => !x.job_id)
  const [detail, setDetail] = useState<string | null>(null)

  return (
    <>
      <Card title={t('Interventi')} actions={
        <>
          <BottoniEsporta path={(fmt) => `/patching/export?what=jobs&fmt=${fmt}`} nome="censo-patch-interventi" disabled={!jobs.length} />
          {canWrite && <button onClick={onNewJob}>{t('Nuovo intervento')}</button>}
        </>
      }>
        {jobs.length === 0 ? (
          <EmptyState title={t('Nessun intervento.')}
            hint={t('Un intervento installa le patch su più macchine insieme: si sceglie cosa, dove e quando, un amministratore lo approva, e CENSO verifica che le patch siano davvero installate.')} />
        ) : (
          <DataTable
            rows={jobs}
            rowKey={(r) => r.id}
            paginazione={{ predefinita: 10 }}
            expanded={(r) => r.id === open}
            expand={(r) => <JobDetail jobId={r.id} onChanged={onChanged} onClose={() => setOpen(null)} />}
            columns={[
              {
                key: 'name', header: t('Intervento'), value: (r) => r.name,
                render: (r) => (
                  <>
                    <button type="button" className="link" onClick={() => setOpen(open === r.id ? null : r.id)}>{r.name}</button>
                    <div className="text-muted patch-sub">{kindLabel(r.kind)} · {jobWhen(r)}</div>
                  </>
                ),
              },
              { key: 'phase', header: t('Stato'), value: (r) => r.phase, render: (r) => <JobPhaseChip phase={r.phase} />, filter: false },
              {
                key: 'progress', header: t('Avanzamento'), value: (r) => r.progress.applied, sort: false, filter: false,
                render: (r) => <ProgressStrip p={r.progress} />,
              },
              {
                key: 'verified', header: t('Verificate'), value: (r) => r.progress.verified,
                render: (r) => `${r.progress.verified}/${r.progress.applied + r.progress.needs_reboot}`,
              },
              {
                key: 'created', header: t('Creato'), value: (r) => r.created_at ?? '',
                render: (r) => <span title={formatDateTime(r.created_at)}>{timeAgo(r.created_at)} · {r.created_by}</span>,
              },
            ]}
          />
        )}
      </Card>
      {loose.length > 0 && (
        <Card title={t('Installazioni singole')}>
          <p className="field__hint" style={{ marginTop: 0 }}>
            {t('Proposte dalla scheda di una macchina, fuori da un intervento.')}
          </p>
          <TaskTable tasks={loose} onOpen={setDetail} />
        </Card>
      )}
      {detail && <TaskDetail taskId={detail} onClose={() => setDetail(null)} onChanged={() => { void single.reload(); onChanged() }} />}
    </>
  )
}

export function TaskTable({ tasks, onOpen }: { tasks: PatchTask[]; onOpen: (id: string) => void }) {
  return (
    <DataTable
      rows={tasks}
      rowKey={(r) => r.id}
      paginazione={{ predefinita: 25 }}
      initialSort={{ key: 'created', dir: 'desc' }}
      columns={[
        {
          key: 'ci', header: t('Macchina'), value: (r) => r.ci_name ?? r.ci_id,
          render: (r) => <Link to={`/cis/${r.ci_id}`}>{r.ci_name ?? r.ci_id.slice(0, 8)}</Link>,
        },
        { key: 'kind', header: t('Cosa'), value: (r) => kindLabel(r.kind), filter: 'select' },
        {
          key: 'status', header: t('Stato'), value: (r) => r.status, filter: false,
          render: (r) => <><TaskStatusChip task={r} /> <VerificationChip v={r.verification} /></>,
        },
        {
          key: 'summary', header: t('Esito'), value: (r) => String(r.result?.summary ?? r.message ?? ''),
          render: (r) => <span className="patch-sub">{String(r.result?.summary ?? r.message ?? '')}</span>,
        },
        {
          key: 'created', header: t('Quando'), value: (r) => r.finished_at ?? r.created_at ?? '',
          render: (r) => formatDateTime(r.finished_at ?? r.created_at),
        },
        {
          key: 'open', header: '', value: () => '', sort: false, filter: false,
          render: (r) => <button className="link" onClick={() => onOpen(r.id)}>{t('Dettagli')}</button>,
        },
      ]}
    />
  )
}

function JobDetail({ jobId, onChanged, onClose }: { jobId: string; onChanged: () => void; onClose: () => void }) {
  const job = useAsync(() => api.patchJob(jobId), [jobId])
  const admin = useIsTenantAdmin()
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [detail, setDetail] = useState<string | null>(null)

  // While something is moving — installs running, dry runs or verifications
  // pending — the page follows it on its own.
  const reload = job.reload
  const moving = Boolean(job.data && (
    ['scheduled', 'running'].includes(job.data.phase)
    || job.data.tasks.some((x) => x.dry_run_requested
      || ['waiting_inventory', 'to_assess'].includes(x.verification?.status ?? ''))))
  useEffect(() => {
    if (!moving) return
    const timer = setInterval(() => { void reload() }, 20000)
    return () => clearInterval(timer)
  }, [moving, reload])

  async function act(fn: () => Promise<unknown>) {
    setError(null)
    setBusy(true)
    try {
      await fn()
      await job.reload()
      onChanged()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  if (job.loading && !job.data) return <Spinner />
  const j = job.data
  if (!j) return <ErrorBanner error={job.error} />
  const kbs = j.spec?.kbs ?? []
  const pkgs = [...(j.spec?.packages ?? []), ...(j.spec?.software ?? [])]
  const p = j.progress

  return (
    <div className="patch-job">
      <div className="patch-job__head">
        <div>
          <p>
            <strong>{kindLabel(j.kind)}</strong>
            {(kbs.length > 0 || pkgs.length > 0) && <span className="mono"> — {[...kbs, ...pkgs].join(', ')}</span>}
          </p>
          <p className="text-muted">
            {j.targets.tag ? t('Macchine con il tag «{t}»', { t: j.targets.tag }) : t('{n} macchine scelte', { n: j.targets.machines })}
            {' · '}{jobWhen(j)}{' · '}{rebootPolicy(j.allow_reboot, j.reboot_delay_min)}
          </p>
          {j.approved_by && <p className="text-muted">{t('Approvato da {u} il {d}', { u: j.approved_by, d: formatDateTime(j.approved_at) })}</p>}
        </div>
        <div className="rowactions">
          {j.phase === 'to_approve' && (
            <button className="secondary" disabled={busy} onClick={() => act(() => api.patchJobDryRun(j.id))}
              title={t('Ogni macchina dice cosa installerebbe, senza toccare niente.')}>{t('Prova prima')}</button>
          )}
          {admin && (j.phase === 'to_approve' || (j.status === 'approved' && p.blocked > 0)) && (
            <button disabled={busy} onClick={() => act(() => api.patchJobApprove(j.id))}>
              {j.phase === 'to_approve' ? t('Approva e avvia') : t('Riprova le bloccate')}
            </button>
          )}
          {admin && p.failed > 0 && j.status !== 'canceled' && (
            <button className="secondary" disabled={busy} onClick={() => act(() => api.patchJobRetry(j.id))}>
              {t('Riprova le non riuscite')}
            </button>
          )}
          {admin && j.status !== 'canceled' && (p.waiting > 0 || p.blocked > 0 || j.phase === 'to_approve') && (
            <button className="secondary" disabled={busy} onClick={() => {
              if (window.confirm(t('Annullare l’intervento? Ciò che non è ancora partito non partirà.'))) {
                void act(() => api.patchJobCancel(j.id))
              }
            }}>{t('Annulla l’intervento')}</button>
          )}
          <button className="secondary" onClick={onClose}>{t('Chiudi')}</button>
        </div>
      </div>
      <ErrorBanner error={error} />
      {j.phase === 'to_approve' && !admin && (
        <p className="field__hint">{t('Da approvare: lo fa un amministratore del tenant. Intanto «Prova prima» mostra cosa installerebbe ogni macchina.')}</p>
      )}
      {j.targets.skipped.length > 0 && (
        <details className="patch-skipped">
          <summary>{j.targets.skipped.length === 1 ? t('1 macchina esclusa') : t('{n} macchine escluse', { n: j.targets.skipped.length })}</summary>
          <ul>
            {j.targets.skipped.map((s) => (
              <li key={s.ci_id}><Link to={`/cis/${s.ci_id}`}>{s.name}</Link> — {reasonLabel(s.reason)}</li>
            ))}
          </ul>
        </details>
      )}
      <DataTable
        rows={j.tasks}
        rowKey={(r) => r.id}
        paginazione={{ predefinita: 25 }}
        columns={[
          {
            key: 'ci', header: t('Macchina'), value: (r) => r.ci_name ?? r.ci_id,
            render: (r) => (
              <>
                <Link to={`/cis/${r.ci_id}`}>{r.ci_name ?? r.ci_id.slice(0, 8)}</Link>
                <div className="text-muted patch-sub">
                  {platformLabel(r.platform)}
                  {r.agent_online === false && ` · ${t('agente non in linea')}`}
                  {r.agent_update && ` · ${t('agente da aggiornare')}`}
                </div>
              </>
            ),
          },
          {
            key: 'status', header: t('Stato'), value: (r) => r.status, filter: 'select',
            render: (r) => <TaskStatusChip task={r} />,
          },
          {
            key: 'verification', header: t('Verifica'), value: (r) => r.verification?.status ?? '',
            render: (r) => <VerificationChip v={r.verification} />, filter: false,
          },
          {
            key: 'summary', header: t('Esito'), value: (r) => String(r.result?.summary ?? r.message ?? ''),
            render: (r) => {
              const dry = r.dry_run as { summary?: string } | null
              const text = String(r.result?.summary ?? '') || r.message || (dry?.summary ? `${t('Prova')}: ${dry.summary}` : '')
                || (r.dry_run_requested ? t('prova chiesta, in attesa dell’agente') : '')
              return <span className="patch-sub">{text}</span>
            },
          },
          {
            key: 'when', header: t('Finita'), value: (r) => r.finished_at ?? '',
            render: (r) => (r.finished_at ? formatDateTime(r.finished_at) : ''),
          },
          {
            key: 'open', header: '', value: () => '', sort: false, filter: false,
            render: (r) => <button className="link" onClick={() => setDetail(r.id)}>{t('Dettagli')}</button>,
          },
        ]}
      />
      {detail && <TaskDetail taskId={detail} onClose={() => setDetail(null)} onChanged={() => { void job.reload(); onChanged() }} />}
    </div>
  )
}
